import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { OrderHistoryDetailsProducts } from '@/components/content/OrderHistoryDetails/parts/OrderHistoryDetailsProducts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import React from 'react';
import { OrderHistoryTrackOrderSteps } from '@/components/content/OrderHistoryDetails/parts/TrackOrderSteps';
import { Linkable } from '@/components/blocks/Linkable';
import { useNotifications } from '@/data/Content/Notifications';
import { OrderItemListRows } from '@/data/Content/OrderItemList';
import {
	OrderHistoryTrackOrderLabelSX,
	OrderHistoryTrackOrderFrameSX,
	OrderHistoryTrackOrderDateSX,
	OrderHistoryTrackOrderDHLButtonSX,
} from '@/components/content/OrderHistoryDetails/styles/track';
import { ShowByCondition } from '@/components/blocks/ShowByCondition';

interface Tracking {
	stage: string;
	end: string | null;
	start: string;
}

interface OrderHistoryTrackOrderProps {
	products: OrderItemListRows;
	trackings: Array<Tracking>;
	orderDate: string;
	estimatedDeliveryDate: string;
	mobile: boolean;
	guide?: string;
	dhlUrl?: string;
}

export enum TRACK_ORDER_STATUS {
	CONFIRMED = 'CONFIRMED',
	IN_PREPARATION = 'IN_PREPARATION',
	ON_THE_WAY = 'ON_THE_WAY',
	DELIVERED = 'DELIVERED',
	UNKNOWN = 'UNKNOWN',
	CANCELLED = 'CANCELLED',
}

export const getTrackOrderStatusByTrackings = (trackings: Array<Tracking>) => {
	const lastStage = trackings.at(-1)?.stage || 'unknown';

	if (['Evaluación', 'Entregado en tienda / Entregado', 'Entregado'].includes(lastStage)) {
		return TRACK_ORDER_STATUS.DELIVERED;
	}

	if (
		[
			'En proceso de entrega',
			'En reparto',
			'En camino por paquetería',
			'En camino por paqueter\u00eda',
			'Pick up de paquetería',
			'Pick up de paqueter\u00eda',
		].includes(lastStage)
	) {
		return TRACK_ORDER_STATUS.ON_THE_WAY;
	}

	if (
		['Preparando pedido (Tienda/CEDIS)', 'Guía generada', 'Gu\u00eda generada'].includes(lastStage)
	) {
		return TRACK_ORDER_STATUS.IN_PREPARATION;
	}

	if (['Pedido procesado', 'Pago aceptado (Facturado dp)'].includes(lastStage)) {
		return TRACK_ORDER_STATUS.CONFIRMED;
	}

	return TRACK_ORDER_STATUS.UNKNOWN;
};

export const OrderHistoryTrackOrder: React.FC<OrderHistoryTrackOrderProps> = ({
	products,
	orderDate,
	estimatedDeliveryDate,
	mobile,
	guide,
	dhlUrl,
	trackings,
}) => {
	const { showSnackbarMessage } = useNotifications();

	const handleOnCopy = () => {
		if (guide) {
			navigator.clipboard.writeText(guide);
			showSnackbarMessage('Copiado al portapapeles');
		}
	};

	const orderTrackStatus = getTrackOrderStatusByTrackings(trackings);
	const theme = useTheme();
	return (
		<Box sx={OrderHistoryTrackOrderFrameSX}>
			<OrderHistoryTrackOrderSteps orderTrackStatus={orderTrackStatus} mobile={mobile} />
			<Box padding={2}>
				<Stack direction="row" justifyContent="space-between" alignItems={'center'}>
					<Stack direction="row" spacing={4}>
						<Stack>
							<Typography sx={OrderHistoryTrackOrderLabelSX}>{'No de guía'}</Typography>
							<ButtonBase
								sx={{
									fontSize: 'var(--font-size-body)',
									fontWeight: 600,
									display: 'flex',
									gap: theme.spacing(0.5),
									color: guide ? theme.palette.primary.main : '#2c2c2c',
									cursor: guide ? 'pointer' : 'default',
									'&:hover': {
										textDecoration: guide ? 'underline' : 'none',
									},
								}}
								disableRipple
								onClick={handleOnCopy}
							>
								{guide || 'No asignado'} {guide ? <ContentCopyIcon fontSize="inherit" /> : null}
							</ButtonBase>
						</Stack>

						{!mobile ? (
							<React.Fragment>
								<Stack>
									<Typography sx={OrderHistoryTrackOrderLabelSX}>{'Fecha de pedido'}</Typography>
									<Typography sx={OrderHistoryTrackOrderDateSX}>{orderDate}</Typography>
								</Stack>
								<ShowByCondition condition={orderTrackStatus !== TRACK_ORDER_STATUS.DELIVERED}>
									<Stack>
										<Typography sx={OrderHistoryTrackOrderLabelSX}>{'Entrega estimada'}</Typography>
										<Typography sx={OrderHistoryTrackOrderDateSX}>
											{estimatedDeliveryDate}
										</Typography>
									</Stack>
								</ShowByCondition>
							</React.Fragment>
						) : null}
					</Stack>
					{dhlUrl ? (
						<Linkable href={dhlUrl} target="_blank">
							<ButtonBase sx={OrderHistoryTrackOrderDHLButtonSX} disableRipple>
								{'Solicitar información'}
								<ArrowForwardIosIcon fontSize="inherit" />
							</ButtonBase>
						</Linkable>
					) : null}
				</Stack>
				<OrderHistoryDetailsProducts items={products} />
			</Box>
		</Box>
	);
};
